/**
 * Bundle Checkout Component
 * 
 * Handles the preflight/checkout page for bundle purchases.
 * Reads bundle data from sessionStorage, displays items, and handles checkout.
 */

// ============================================================================
// DEBUG MODE - Set to true to enable console logging
// ============================================================================
const DEBUG_MODE = false;

// Debug logger utilities - only log when DEBUG_MODE is true
const debugLog = (...args) => DEBUG_MODE && console.log(...args);
const debugWarn = (...args) => DEBUG_MODE && console.warn(...args);
const debugError = (...args) => console.error(...args); // Always show errors

// Storage key must match bundle-product.js
const BUNDLE_STORAGE_KEY = 'bundle_selections';

/**
 * Load theme settings for dynamic text strings
 * Falls back to defaults if settings are not available
 * @returns {Object}
 */
function loadCheckoutSettings() {
  const defaultSettings = {
    swapButtonText: 'Swap item',
    sizeLabel: 'Size: ',
    percentOffSuffix: '% Off',
    checkoutButtonText: 'Proceed to Checkout',
    processingText: 'Processing...',
    errorMessage: 'There was an error processing your order. Please try again.',
    hideSubscribeItemPricing: false,
    afterAddBehavior: 'go_to_checkout'
  };

  try {
    const settingsEl = document.getElementById('bundle-checkout-settings');
    if (settingsEl) {
      const parsed = JSON.parse(settingsEl.textContent);
      return { ...defaultSettings, ...parsed };
    }
  } catch (e) {
    debugWarn('[BundleCheckout] Could not parse settings, using defaults:', e);
  }

  return defaultSettings;
}

/**
 * Get the roundCents setting from theme settings
 * @returns {boolean}
 */
function getRoundCentsSetting() {
  try {
    const settingsEl = document.getElementById('bundle-checkout-settings');
    if (settingsEl) {
      const parsed = JSON.parse(settingsEl.textContent);
      return parsed.roundCents !== false; // Default to true if not set
    }
  } catch (e) {
    debugWarn('[BundleCheckout] Could not parse roundCents setting:', e);
  }
  return true; // Default to rounding
}

/**
 * Format price in cents to currency string
 * @param {number} priceInCents 
 * @param {boolean} roundCents - Whether to round to nearest dollar (optional, uses theme setting if not provided)
 * @returns {string}
 */
function formatMoney(priceInCents, roundCents = null) {
  if (typeof priceInCents !== 'number') return '–';
  
  // Use provided value or fall back to theme setting
  const shouldRound = roundCents !== null ? roundCents : getRoundCentsSetting();
  
  const options = {
    style: 'currency',
    currency: 'USD'
  };
  
  if (shouldRound) {
    options.minimumFractionDigits = 0;
    options.maximumFractionDigits = 0;
    // Round to nearest dollar
    priceInCents = Math.round(priceInCents / 100) * 100;
  }
  
  return new Intl.NumberFormat('en-US', options).format(priceInCents / 100);
}

/**
 * Normalize size display: replace XXL with 2XL and XXXL with 3XL
 * @param {string} size 
 * @returns {string}
 */
function normalizeSize(size) {
  if (!size || typeof size !== 'string') return size;
  return size
    .replace(/\bXXXL\b/gi, '3XL')
    .replace(/\bXXL\b/gi, '2XL');
}

/**
 * Format size for display, adding inch symbol for waist-inseam sizes
 * Converts "32x30" to "32"x30" format
 * @param {string} size - The size value
 * @returns {string} - Formatted size string for display
 */
function formatSizeForDisplay(size) {
  if (!size || typeof size !== 'string') return size || '';
  
  // First normalize the size (XXL → 2XL, etc.)
  const normalized = normalizeSize(size);
  
  // Check if it's a waist-inseam format (e.g., "32x30", "34x32")
  const waistInseamMatch = normalized.match(/^(\d+)x(\d+)$/i);
  if (waistInseamMatch) {
    const waist = waistInseamMatch[1];
    const inseam = waistInseamMatch[2];
    return `${waist}"x${inseam}`;
  }
  
  return normalized;
}

/**
 * Remove vendor name from product title if present
 * Handles common patterns like "Vendor - Title", "Vendor | Title", "Title by Vendor", etc.
 * @param {string} title - The product title
 * @param {string} vendor - The vendor name
 * @returns {string} - The cleaned title
 */
function removeVendorFromTitle(title, vendor) {
  if (!title || typeof title !== 'string') return title;
  if (!vendor || typeof vendor !== 'string') return title;
  
  let cleanTitle = title;
  
  // Remove common patterns: "Vendor - ", "Vendor | ", "Vendor: "
  cleanTitle = cleanTitle.replace(new RegExp(`^${escapeRegExp(vendor)}\\s*[-|:]\\s*`, 'i'), '');
  
  // Remove patterns at the end: " by Vendor", " from Vendor"
  cleanTitle = cleanTitle.replace(new RegExp(`\\s+(by|from)\\s+${escapeRegExp(vendor)}$`, 'i'), '');
  
  // Remove standalone vendor name (case-insensitive)
  cleanTitle = cleanTitle.replace(new RegExp(`^${escapeRegExp(vendor)}\\s*`, 'i'), '');
  cleanTitle = cleanTitle.replace(new RegExp(`\\s*${escapeRegExp(vendor)}$`, 'i'), '');
  
  return cleanTitle.trim();
}

/**
 * Escape special regex characters in a string
 * @param {string} string 
 * @returns {string}
 */
function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Bundle Checkout Web Component
 */
class BundleCheckout extends HTMLElement {
  constructor() {
    super();
    this.bundleData = null;
    this.selectedCheckoutType = 'subscribe'; // Default
    this.settings = loadCheckoutSettings(); // Load CMS settings for text strings
    
    // Bind methods
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleCheckoutTypeChange = this.handleCheckoutTypeChange.bind(this);
    this.handleSwapClick = this.handleSwapClick.bind(this);
    this.handleSwapConfirmed = this.handleSwapConfirmed.bind(this);
    this.handleSwapsComplete = this.handleSwapsComplete.bind(this);
    this.handleBackClick = this.handleBackClick.bind(this);
  }

  connectedCallback() {
    debugLog('[BundleCheckout] Connected');
    
    // Cache elements
    this.cacheElements();
    
    // Load bundle data from sessionStorage
    this.loadBundleData();
    
    // Attach event listeners
    this.attachListeners();
  }

  disconnectedCallback() {
    this.detachListeners();
  }

  cacheElements() {
    this.itemsGrid = this.querySelector('[data-items-grid]');
    this.itemsCarousel = this.querySelector('[data-items-carousel]');
    this.loadingEl = this.querySelector('[data-loading]');
    this.emptyState = this.querySelector('[data-empty-state]');
    this.form = this.querySelector('[data-checkout-form]');
    this.checkoutBtn = this.querySelector('[data-checkout-button]');
    this.subscribePrice = this.querySelector('[data-subscribe-price]');
    this.fullPriceTotal = this.querySelector('[data-total-price]');
    this.radioInputs = this.querySelectorAll('input[name="checkout-type"]');
    this.swapIcon = this.dataset.swapIcon || '';
    this.savingsPercentEl = this.querySelector('[data-savings-percent]');
    this.backButtons = this.querySelectorAll('[data-back-button]');

    debugLog('[BundleCheckout] cacheElements() - Elements found:', {
      itemsGrid: !!this.itemsGrid,
      itemsCarousel: !!this.itemsCarousel,
      form: !!this.form,
      fullPriceTotal: !!this.fullPriceTotal,
      savingsPercentEl: !!this.savingsPercentEl,
      radioInputsCount: this.radioInputs?.length || 0
    });
  }

  attachListeners() {
    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', this.handleFormSubmit);
    }

    // Radio inputs
    this.radioInputs.forEach(radio => {
      radio.addEventListener('change', this.handleCheckoutTypeChange);
    });

    // Back buttons (mobile and desktop)
    this.backButtons.forEach(btn => {
      btn.addEventListener('click', this.handleBackClick);
    });

    // Listen for swap events from alternates modal
    document.addEventListener('bundle:swap-confirmed', this.handleSwapConfirmed);
    document.addEventListener('bundle:swaps-complete', this.handleSwapsComplete);
  }

  detachListeners() {
    if (this.form) {
      this.form.removeEventListener('submit', this.handleFormSubmit);
    }
    this.radioInputs.forEach(radio => {
      radio.removeEventListener('change', this.handleCheckoutTypeChange);
    });
    this.backButtons.forEach(btn => {
      btn.removeEventListener('click', this.handleBackClick);
    });
    document.removeEventListener('bundle:swap-confirmed', this.handleSwapConfirmed);
    document.removeEventListener('bundle:swaps-complete', this.handleSwapsComplete);
  }

  /**
   * Handle back button click - navigate to previous page
   */
  handleBackClick() {
    // Use browser history to go back
    if (window.history.length > 1) {
      window.history.back();
    } else {
      // Fallback: go to homepage if no history
      window.location.href = '/';
    }
  }

  /**
   * Load bundle data from sessionStorage
   */
  loadBundleData() {
    try {
      const saved = sessionStorage.getItem(BUNDLE_STORAGE_KEY);
      
      if (!saved) {
        debugLog('[BundleCheckout] No bundle data found in sessionStorage');
        this.showEmptyState();
        return;
      }

      this.bundleData = JSON.parse(saved);
      debugLog('[BundleCheckout] ========== LOADED BUNDLE DATA ==========');
      debugLog('[BundleCheckout] Full data:', this.bundleData);
      debugLog('[BundleCheckout] Items count:', this.bundleData.items?.length || 0);
      debugLog('[BundleCheckout] Categories:', this.bundleData.categories);
      debugLog('[BundleCheckout] Selections:', this.bundleData.selections);
      debugLog('[BundleCheckout] Plan:', this.bundleData.plan);
      debugLog('[BundleCheckout] Is complete:', this.bundleData.isComplete);

      // Validate bundle data
      if (!this.bundleData.items || this.bundleData.items.length === 0) {
        debugLog('[BundleCheckout] ❌ Bundle has no items');
        debugLog('[BundleCheckout] This usually means variant matching failed on the PDP');
        this.showEmptyState();
        return;
      }
      
      // Store original items (deep copy) so we can offer "swap back" to primary
      // Only set if not already set (allows persisting across page refreshes)
      if (!this.bundleData.originalItems) {
        this.bundleData.originalItems = JSON.parse(JSON.stringify(this.bundleData.items));
        // Save back to sessionStorage so originalItems persists
        this.saveBundleData();
        debugLog('[BundleCheckout] Stored original items for swap-back functionality');
      } else {
        debugLog('[BundleCheckout] Original items already stored');
      }
      
      debugLog('[BundleCheckout] ✅ Bundle items:');
      this.bundleData.items.forEach((item, i) => {
        debugLog(`  [${i}] ${item.productTitle} - Variant: ${item.variantId} (${item.variantTitle})`);
      });

      // Render items
      this.renderItems();
      
      // Calculate and display prices
      this.updatePrices();
      
      // Hide loading
      if (this.loadingEl) {
        this.loadingEl.hidden = true;
      }

    } catch (e) {
      debugError('[BundleCheckout] Error loading bundle data:', e);
      this.showEmptyState();
    }
  }

  /**
   * Show empty state when no bundle data exists
   */
  showEmptyState() {
    if (this.loadingEl) this.loadingEl.hidden = true;
    if (this.emptyState) this.emptyState.hidden = false;
    
    // Hide the main content
    const container = this.querySelector('.bundle-checkout__container');
    if (container) container.hidden = true;
  }

  /**
   * Render bundle items to grid and carousel
   */
  renderItems() {
    if (!this.bundleData?.items) return;

    const items = this.bundleData.items;
    
    // Generate HTML for each item
    const itemsHTML = items.map((item, index) => this.renderItemCard(item, index)).join('');
    
    // Render to grid (desktop)
    if (this.itemsGrid) {
      this.itemsGrid.innerHTML = itemsHTML;
    }

    // Render to carousel (mobile) - wrap in swiper-slide
    if (this.itemsCarousel) {
      const swiperWrapper = this.itemsCarousel.querySelector('.swiper-wrapper');
      if (swiperWrapper) {
        swiperWrapper.innerHTML = items.map((item, index) => 
          `<div class="swiper-slide">${this.renderItemCard(item, index)}</div>`
        ).join('');
      }

      // Reinitialize swiper after content is loaded
      if (window.reinitSwipers) {
        const carouselSection = this.itemsCarousel.closest('.swiper-section');
        if (carouselSection) {
          window.reinitSwipers(carouselSection);
        }
      }
    }

    // Attach swap button listeners
    this.attachSwapListeners();
  }

  /**
   * Get total of all items' original prices (what they cost individually)
   * @returns {number} Total price in cents
   */
  getTotalItemsPrice() {
    if (!this.bundleData?.items) return 0;
    return this.bundleData.items.reduce((sum, item) => {
      return sum + (item.originalPrice || item.price || 0);
    }, 0);
  }

  /**
   * Get total of all items' compare-at/retail prices
   * This is the "full retail value" of the bundle
   * @returns {number} Total retail price in cents
   */
  getTotalRetailPrice() {
    if (!this.bundleData?.items) return 0;
    return this.bundleData.items.reduce((sum, item) => {
      // Use compareAtPrice if available, otherwise fall back to originalPrice/price
      return sum + (item.compareAtPrice || item.originalPrice || item.price || 0);
    }, 0);
  }

  /**
   * Get the discount multiplier for subscribe mode
   * Plan Price ÷ Total Items Price
   * This ensures sum of (itemPrice × multiplier) = planPrice
   * @returns {number} Multiplier (e.g., 0.72 means 28% off item prices)
   */
  getDiscountMultiplier() {
    const planPrice = this.bundleData?.plan?.variantPrice || 0;
    const totalItemsPrice = this.getTotalItemsPrice();
    if (totalItemsPrice === 0) return 1;
    return planPrice / totalItemsPrice;
  }

  /**
   * Get the savings percentage for subscribe mode (vs buying items individually)
   * @returns {number} Percentage off (e.g., 28 for 28% off)
   */
  getSavingsPercent() {
    const multiplier = this.getDiscountMultiplier();
    // Ensure we don't show negative savings if plan is more expensive than items
    const savings = Math.round((1 - multiplier) * 100);
    return Math.max(0, savings);
  }

  /**
   * Get the dynamic per-item price when in subscribe mode
   * Applies proportional discount: Item Original Price × Discount Multiplier
   * @param {Object} item - The item to calculate price for
   * @returns {number} Price in cents
   */
  getDynamicItemPrice(item) {
    // Use originalPrice (item's actual price) as the base for discount calculation
    const itemPrice = item.originalPrice || item.price || 0;
    const multiplier = this.getDiscountMultiplier();
    return Math.round(itemPrice * multiplier);
  }

  /**
   * Determine if item prices should be shown
   * Returns false when hideSubscribeItemPricing is enabled AND subscribe mode is selected
   * @returns {boolean}
   */
  shouldShowItemPrices() {
    const hideInSubscribeMode = this.settings.hideSubscribeItemPricing === true;
    const isSubscribeMode = this.selectedCheckoutType === 'subscribe';
    
    // Hide prices only if setting is enabled AND user is in subscribe mode
    if (hideInSubscribeMode && isSubscribeMode) {
      return false;
    }
    
    return true;
  }

  /**
   * Render a single item card
   * @param {Object} item 
   * @param {number} index 
   * @returns {string}
   */
  /**
   * Check whether a bundle item has any swap alternates that match the user's
   * selected size. Delegates to the modal's checkVariantSizeMatch for accuracy
   * (handles sibling variants, size equivalence mapping, waist-inseam, etc.).
   * @param {Object} item
   * @returns {boolean}
   */
  hasAlternatesForItem(item) {
    const alternates = this.bundleData?.alternates;
    if (!alternates || !alternates.length || item.itemNumber == null) return false;

    const selectedSize = this.getSelectedSizeForItem(item);
    const sizingType = item.sizingType || 'standard';
    const modal = this._alternatesModal || (this._alternatesModal = document.querySelector('bundle-alternates-modal'));

    return alternates.some(alt => {
      if (alt.itemNumber !== item.itemNumber) return false;
      if (String(alt.productId) === String(item.productId)) return false;

      // Use the modal's size-matching logic when available
      if (modal && typeof modal.checkVariantSizeMatch === 'function' && selectedSize) {
        return modal.checkVariantSizeMatch(alt, selectedSize, sizingType);
      }

      // Fallback if modal not ready or no size selected: assume available
      return true;
    });
  }

  renderItemCard(item, index) {
    const itemType = item.itemType || this.deriveItemType(item);
    const isSubscribeMode = this.selectedCheckoutType === 'subscribe';
    
    // Determine display price based on checkout mode
    // Subscribe mode: proportional discount (item retail price × discount multiplier)
    // Full price mode: item's actual price
    const displayPrice = isSubscribeMode 
      ? this.getDynamicItemPrice(item) 
      : (item.originalPrice || item.price);
    
    // Compare price is the item's retail/compare-at price (crossed out)
    // In subscribe mode: show full retail price (compareAtPrice) crossed out
    // In full price mode: show compare_at_price crossed out (if exists and higher than price)
    const comparePrice = item.compareAtPrice || 0;
    const hasComparePrice = comparePrice && comparePrice > displayPrice;
    
    // Calculate percentage off for this specific item
    let percentOff = 0;
    if (hasComparePrice) {
      percentOff = Math.round((1 - displayPrice / comparePrice) * 100);
    }

    // DEBUG: Log pricing calculations for each item
    debugLog(`[BundleCheckout] Card #${index} - ${item.productTitle}:`, {
      checkoutMode: this.selectedCheckoutType,
      isSubscribeMode,
      planPrice: this.bundleData?.plan?.variantPrice,
      totalItemsPrice: this.getTotalItemsPrice(),
      discountMultiplier: this.getDiscountMultiplier(),
      savingsPercent: this.getSavingsPercent(),
      'item.price': item.price,
      'item.originalPrice': item.originalPrice,
      'item.compareAtPrice': item.compareAtPrice,
      displayPrice,
      comparePrice,
      hasComparePrice,
      percentOff,
      vendor: item.productVendor
    });
    
    // Extract size from variantTitle (usually first option before " / ") and format with inch symbol for waist-inseam
    const rawSize = item.variantTitle ? item.variantTitle.split(' / ')[0] : '';
    const sizeDisplay = formatSizeForDisplay(rawSize);

    return `
      <div class="bundle-checkout__item" 
           data-item-index="${index}"
           data-product-id="${item.productId}"
           data-variant-id="${item.variantId}"
           data-item-type="${itemType || 'unknown'}"
           data-category-key="${item.categoryKey || ''}"
           data-sizing-type="${item.sizingType || ''}">
        <div class="bundle-checkout__item-image">
          ${item.featuredImage 
            ? `<img src="${item.featuredImage}" alt="${removeVendorFromTitle(item.productTitle, item.productVendor)}" loading="lazy">`
            : '<div class="bundle-checkout__item-placeholder"></div>'
          }
        </div>
        <div class="bundle-checkout__item-info">
          ${item.productVendor ? `<span class="bundle-checkout__item-vendor">${item.productVendor}</span>` : ''}
          <span class="bundle-checkout__item-title">${removeVendorFromTitle(item.productTitle, item.productVendor)}</span>
          <div class="bundle-checkout__item-prices">
            ${this.shouldShowItemPrices() 
              ? `<span class="bundle-checkout__item-price">${formatMoney(displayPrice)}</span>
                 ${hasComparePrice 
                   ? `<span class="bundle-checkout__item-compare-price">${formatMoney(comparePrice)}</span>` 
                   : ''
                 }`
              : ''
            }
            ${percentOff > 0 ? `<span class="bundle-checkout__item-badge bundle-checkout__item-badge--sale">${percentOff}${this.settings.percentOffSuffix}</span>` : ''}
          </div>
          ${sizeDisplay ? `<span class="bundle-checkout__item-size">${this.settings.sizeLabel}${sizeDisplay}</span>` : ''}
          ${this.hasAlternatesForItem(item)
            ? `<button type="button" class="bundle-checkout__item-swap" data-swap-item="${index}">
                ${this.settings.swapButtonText}${this.swapIcon ? `<img src="${this.swapIcon}" alt="" aria-hidden="true">` : ''}
              </button>`
            : ''
          }
        </div>
      </div>
    `;
  }

  /**
   * Attach click listeners to swap buttons
   */
  attachSwapListeners() {
    const swapButtons = this.querySelectorAll('[data-swap-item]');
    swapButtons.forEach(btn => {
      btn.addEventListener('click', this.handleSwapClick);
    });
  }

  /**
   * Handle swap button click
   * @param {Event} event 
   */
  handleSwapClick(event) {
    const btn = event.currentTarget;
    const itemIndex = parseInt(btn.dataset.swapItem, 10);
    const item = this.bundleData?.items?.[itemIndex];

    if (!item) {
      debugError('[BundleCheckout] Item not found at index:', itemIndex);
      return;
    }

    debugLog('[BundleCheckout] Swap clicked for item:', item);
    debugLog('[BundleCheckout] Item itemNumber:', item.itemNumber);
    debugLog('[BundleCheckout] Item categoryKey:', item.categoryKey);
    debugLog('[BundleCheckout] Item sizingType:', item.sizingType);

    // Get the original item for this slot (allows "swap back to original" functionality)
    const originalItem = this.bundleData?.originalItems?.[itemIndex];
    const isCurrentlySwapped = originalItem && 
      String(originalItem.variantId) !== String(item.variantId);
    
    debugLog('[BundleCheckout] Original item:', originalItem?.productTitle);
    debugLog('[BundleCheckout] Is currently swapped:', isCurrentlySwapped);

    // Get the alternates modal manager instance
    const ManagerClass = window.BundleAlternatesManager;
    if (!ManagerClass) {
      debugError('[BundleCheckout] BundleAlternatesManager class not available');
      return;
    }
    
    const manager = ManagerClass.getInstance();

    // Queue this single item for swapping with 'optional' mode (user-initiated)
    // IMPORTANT: itemNumber is the bundle slot position (1-6) used to filter alternates
    manager.queueSwaps([{
      productId: item.productId,
      productHandle: item.productHandle,
      productTitle: item.productTitle,
      productVendor: item.productVendor || '',
      variantId: item.variantId,
      variantTitle: item.variantTitle,
      itemNumber: item.itemNumber, // Bundle slot position (1-6) for filtering alternates
      itemType: item.itemType || this.deriveItemType(item),
      type: item.type || null, // Product type for type-aware alternate filtering
      category: item.category,
      categoryKey: item.categoryKey,
      sizingType: item.sizingType,
      selectedSize: this.getSelectedSizeForItem(item),
      featuredImage: item.featuredImage,
      itemIndex: itemIndex, // Track which item in the array
      // Include original item for "swap back" functionality
      originalItem: isCurrentlySwapped ? originalItem : null
    }], { mode: 'optional' });
  }

  /**
   * Get the selected size for a given bundle item
   * @param {Object} item
   * @returns {string|Object|null}
   */
  getSelectedSizeForItem(item) {
    if (!this.bundleData?.selections) return null;

    const selections = this.bundleData.selections;
    const categoryKey = item?.categoryKey;
    const sizingType = item?.sizingType || 'standard';
    const categorySelection = categoryKey ? selections[categoryKey] : null;

    // New category-based selection structure
    if (categorySelection) {
      if (sizingType === 'waist-inseam') {
        const waist = categorySelection.waist;
        const inseam = categorySelection.inseam;
        if (waist && inseam) {
          return { waist, inseam, combined: `${waist}x${inseam}` };
        }
        return null;
      }

      return categorySelection.size || null;
    }

    // Legacy fallback (top/bottom/shoe)
    const itemType = item?.itemType || this.deriveItemType(item);
    switch (itemType) {
      case 'top':
        return selections.top || null;
      case 'bottom':
        if (selections.waist && selections.inseam) {
          return `${selections.waist}x${selections.inseam}`;
        }
        return selections.waist || null;
      case 'shoe':
        return selections.shoe || null;
      default:
        return null;
    }
  }

  /**
   * Derive legacy itemType from sizingType/category when itemType is missing
   * @param {Object} item
   * @returns {string} 'top' | 'bottom' | 'shoe' | 'unknown'
   */
  deriveItemType(item) {
    if (!item) return 'unknown';
    if (item.sizingType === 'waist-inseam') return 'bottom';
    if (item.sizingType === 'shoe') return 'shoe';
    return 'top';
  }

  /**
   * Handle swap confirmed event - update the specific item
   * @param {CustomEvent} event 
   */
  handleSwapConfirmed(event) {
    const { original, replacement } = event.detail;
    
    debugLog('[BundleCheckout] Swap confirmed:', event.detail);

    // Find the item index in our bundle data using the itemIndex we passed when queueing
    const itemIndex = original?.itemIndex;
    
    if (itemIndex === undefined || !this.bundleData?.items?.[itemIndex]) {
      debugError('[BundleCheckout] Could not find item to swap at index:', itemIndex);
      return;
    }

    // Get the new item data from alternates
    const modal = document.querySelector('bundle-alternates-modal');
    const alternatesData = modal?.alternatesData || [];
    
    // Try to find the alternate by variantId first (exact match)
    // If not found, try to find by productId (for sibling variant swaps where variantId differs)
    let newAlternate = alternatesData.find(alt => 
      String(alt.variantId) === String(replacement?.variantId)
    );
    
    // If not found by variantId, find by productId (sibling variant case)
    if (!newAlternate && replacement?.productId) {
      newAlternate = alternatesData.find(alt => 
        String(alt.productId) === String(replacement.productId)
      );
      if (newAlternate) {
        debugLog('[BundleCheckout] Found alternate by productId (sibling variant)');
      }
    }

    // Preserve the original itemNumber (bundle slot position) - it should stay the same after swap
    const originalItemNumber = this.bundleData.items[itemIndex].itemNumber;
    
    if (newAlternate) {
      // Update the item in bundle data with full alternate info
      // Use replacement.variantId/variantTitle (the actual selected variant) but alternate's product info
      this.bundleData.items[itemIndex] = {
        ...this.bundleData.items[itemIndex],
        productId: newAlternate.productId,
        productHandle: newAlternate.productHandle,
        productTitle: newAlternate.productTitle,
        productVendor: newAlternate.productVendor || '',
        variantId: replacement.variantId || newAlternate.variantId, // Use the actual selected variant
        variantTitle: replacement.variantTitle || newAlternate.variantTitle,
        price: replacement.price || newAlternate.variantPrice,
        originalPrice: replacement.price || newAlternate.variantPrice, // Preserved original price for full-price checkout
        compareAtPrice: newAlternate.variantCompareAtPrice || 0,
        available: newAlternate.variantAvailable,
        featuredImage: replacement.featuredImage || newAlternate.featuredImage, // Use replacement image if provided
        category: newAlternate.category,
        categoryKey: newAlternate.categoryKey,
        sizingType: newAlternate.sizingType,
        itemType: newAlternate.itemType,
        itemNumber: originalItemNumber // Preserve original bundle slot position
      };
      debugLog('[BundleCheckout] Updated item with alternate data:', this.bundleData.items[itemIndex]);
    } else if (replacement) {
      // Fallback: use what we have from the replacement object
      const newPrice = replacement.price || this.bundleData.items[itemIndex].price;
      this.bundleData.items[itemIndex] = {
        ...this.bundleData.items[itemIndex],
        productId: replacement.productId,
        productHandle: replacement.productHandle,
        productTitle: replacement.productTitle,
        productVendor: replacement.productVendor || this.bundleData.items[itemIndex].productVendor || '',
        variantId: replacement.variantId,
        variantTitle: replacement.variantTitle,
        price: newPrice,
        originalPrice: newPrice, // Preserved original price for full-price checkout
        compareAtPrice: replacement.compareAtPrice || this.bundleData.items[itemIndex].compareAtPrice || 0,
        available: true, // Assume available if selected
        featuredImage: replacement.featuredImage || this.bundleData.items[itemIndex].featuredImage, // Include featuredImage
        category: replacement.category || this.bundleData.items[itemIndex].category,
        categoryKey: replacement.categoryKey || this.bundleData.items[itemIndex].categoryKey,
        sizingType: replacement.sizingType || this.bundleData.items[itemIndex].sizingType,
        itemType: replacement.itemType || this.bundleData.items[itemIndex].itemType,
        itemNumber: originalItemNumber // Preserve original bundle slot position
      };
      debugLog('[BundleCheckout] Updated item with replacement data:', this.bundleData.items[itemIndex]);
    }

    // Save updated data to sessionStorage
    this.saveBundleData();
    
    // Immediately re-render to show the update
    this.renderItems();
    this.updatePrices();
  }

  /**
   * Handle swaps complete - refresh the display
   */
  handleSwapsComplete() {
    debugLog('[BundleCheckout] All swaps complete, refreshing display');
    
    // Re-render items with updated data
    this.renderItems();
    
    // Update prices
    this.updatePrices();
  }

  /**
   * Save current bundle data to sessionStorage
   */
  saveBundleData() {
    try {
      sessionStorage.setItem(BUNDLE_STORAGE_KEY, JSON.stringify(this.bundleData));
      debugLog('[BundleCheckout] Saved updated bundle data');
    } catch (e) {
      debugError('[BundleCheckout] Error saving bundle data:', e);
    }
  }

  /**
   * Update displayed prices
   */
  updatePrices() {
    if (!this.bundleData?.items) {
      debugWarn('[BundleCheckout] updatePrices() - No items in bundleData');
      return;
    }

    // Calculate totals
    const totalItemsPrice = this.getTotalItemsPrice();  // Sum of individual item prices
    const totalRetailPrice = this.getTotalRetailPrice(); // Sum of compare-at prices (full retail)
    const planPrice = this.bundleData.plan?.variantPrice || 0;
    const discountMultiplier = this.getDiscountMultiplier();
    const savingsPercent = this.getSavingsPercent();

    debugLog('[BundleCheckout] ========== PRICING DEBUG ==========');
    debugLog('[BundleCheckout] Items breakdown:');
    this.bundleData.items.forEach((item, i) => {
      const itemOriginal = item.originalPrice || item.price || 0;
      const itemCompare = item.compareAtPrice || 0;
      const itemDiscounted = Math.round(itemOriginal * discountMultiplier);
      const itemPercentOff = itemCompare > 0 ? Math.round((1 - itemDiscounted / itemCompare) * 100) : 0;
      debugLog(`  [${i}] ${item.productTitle}:`);
      debugLog(`       originalPrice=${formatMoney(itemOriginal)}, compareAtPrice=${formatMoney(itemCompare)}`);
      debugLog(`       Subscribe price: ${formatMoney(itemDiscounted)} (${itemPercentOff}% off retail)`);
    });
    debugLog('[BundleCheckout] Totals:', {
      totalItemsPrice: formatMoney(totalItemsPrice) + ` (${totalItemsPrice})`,
      totalRetailPrice: formatMoney(totalRetailPrice) + ` (${totalRetailPrice})`,
      planPrice: formatMoney(planPrice) + ` (${planPrice})`,
      discountMultiplier: discountMultiplier.toFixed(4) + ` (${savingsPercent}% off items price)`
    });

    // Update full price display (sum of individual item prices)
    debugLog('[BundleCheckout] fullPriceTotal element:', this.fullPriceTotal);
    if (this.fullPriceTotal) {
      this.fullPriceTotal.textContent = formatMoney(totalItemsPrice);
      debugLog('[BundleCheckout] ✅ Set full price to:', formatMoney(totalItemsPrice));
    } else {
      debugWarn('[BundleCheckout] ❌ fullPriceTotal element not found');
    }

    // Update full price retail display (sum of compare-at prices)
    const fullPriceRetailEl = this.querySelector('[data-total-price-retail]');
    debugLog('[BundleCheckout] fullPriceRetailEl ([data-total-price-retail]):', fullPriceRetailEl);
    if (fullPriceRetailEl && totalRetailPrice > 0) {
      fullPriceRetailEl.textContent = formatMoney(totalRetailPrice);
      debugLog('[BundleCheckout] ✅ Set full price retail to:', formatMoney(totalRetailPrice));
    } else if (fullPriceRetailEl) {
      fullPriceRetailEl.textContent = '';
      debugLog('[BundleCheckout] Cleared full price retail (no retail prices)');
    }

    // Update subscribe price from plan data in sessionStorage
    const priceValueEl = this.querySelector('[data-plan-price-value]');
    debugLog('[BundleCheckout] priceValueEl ([data-plan-price-value]):', priceValueEl);
    if (planPrice) {
      const priceText = formatMoney(planPrice);
      
      if (priceValueEl) {
        priceValueEl.textContent = priceText;
        debugLog('[BundleCheckout] ✅ Set subscribe price to:', priceText);
      } else {
        debugWarn('[BundleCheckout] ❌ [data-plan-price-value] element not found');
      }
    } else {
      debugWarn('[BundleCheckout] ❌ No plan.variantPrice in bundleData');
    }

    // Update subscribe retail/compare-at price (sum of all items' compare-at prices)
    const retailPriceEl = this.querySelector('[data-plan-price-value-retail]');
    debugLog('[BundleCheckout] retailPriceEl ([data-plan-price-value-retail]):', retailPriceEl);
    if (totalRetailPrice > 0) {
      const retailPriceText = formatMoney(totalRetailPrice);
      
      if (retailPriceEl) {
        retailPriceEl.textContent = retailPriceText;
        debugLog('[BundleCheckout] ✅ Set retail price to:', retailPriceText);
      } else {
        debugWarn('[BundleCheckout] ❌ [data-plan-price-value-retail] element not found');
      }
    } else {
      debugWarn('[BundleCheckout] ❌ No compareAtPrice values found on items');
    }

    // Update savings percentage in the Subscribe & Save badge
    debugLog('[BundleCheckout] savingsPercentEl ([data-savings-percent]):', this.savingsPercentEl);
    if (this.savingsPercentEl && savingsPercent > 0) {
      this.savingsPercentEl.textContent = `– ${savingsPercent}% Off`;
      debugLog('[BundleCheckout] ✅ Set savings badge to:', savingsPercent + '% Off');
    } else if (this.savingsPercentEl) {
      this.savingsPercentEl.textContent = '';
      debugLog('[BundleCheckout] Cleared savings badge (savingsPercent=' + savingsPercent + ')');
    } else {
      debugWarn('[BundleCheckout] ❌ [data-savings-percent] element not found');
    }
    debugLog('[BundleCheckout] ====================================');
  }

  /**
   * Handle checkout type radio change
   * @param {Event} event 
   */
  handleCheckoutTypeChange(event) {
    this.selectedCheckoutType = event.target.value;
    debugLog('[BundleCheckout] Checkout type changed to:', this.selectedCheckoutType);
    
    // Re-render items to update pricing display
    this.renderItems();
  }

  /**
   * Remove any existing bundle products from the cart before adding a new one.
   * Uses multiple detection methods because Shopify may hide underscore-prefixed
   * properties from the /cart.js response.
   * @returns {Promise<void>}
   */
  async removeExistingBundleItems() {
    try {
      const response = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      });
      const cart = await response.json();

      if (!cart.items || cart.items.length === 0) {
        debugLog('[BundleCheckout] Cart is empty, no bundle items to remove');
        return;
      }

      const bundleProductId = this.bundleData?.bundleProductId;

      debugLog('[BundleCheckout] Scanning cart for existing bundles...');
      debugLog('[BundleCheckout] Bundle product ID to match:', bundleProductId);
      cart.items.forEach((item, i) => {
        debugLog(`  [${i}] key=${item.key} variant=${item.id} product_id=${item.product_id} title="${item.title}" properties=`, item.properties);
      });

      const bundleLineItems = cart.items.filter(item => {
        // Method 1: Match by bundle properties (any property key containing "bundle")
        // Shopify may omit underscore-prefixed keys, so check broadly
        if (item.properties && typeof item.properties === 'object') {
          const hasBundleProp = Object.keys(item.properties).some(key =>
            key.toLowerCase().includes('bundle')
          );
          if (hasBundleProp) {
            debugLog('[BundleCheckout] Matched by properties:', item.key);
            return true;
          }
        }

        // Method 2: Match by product ID — all bundle "looks" share the same
        // Shopify product, so any cart item from that product is a bundle
        if (bundleProductId && String(item.product_id) === String(bundleProductId)) {
          debugLog('[BundleCheckout] Matched by product_id:', item.key);
          return true;
        }

        return false;
      });

      if (bundleLineItems.length === 0) {
        debugLog('[BundleCheckout] No existing bundle items found in cart');
        return;
      }

      debugLog('[BundleCheckout] Found', bundleLineItems.length, 'bundle item(s) to remove');

      const updates = {};
      bundleLineItems.forEach(item => {
        debugLog('[BundleCheckout] Queuing removal:', item.key, item.title);
        updates[item.key] = 0;
      });

      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      const updateResult = await updateResponse.json();

      debugLog('[BundleCheckout] Removal complete. Remaining cart items:', updateResult.items?.length);
    } catch (error) {
      debugError('[BundleCheckout] Error removing existing bundle items:', error);
      // Don't throw — proceed with adding the new bundle even if removal fails
    }
  }

  /**
   * Handle form submission
   * @param {Event} event 
   */
  async handleFormSubmit(event) {
    event.preventDefault();

    if (!this.bundleData?.items || this.bundleData.items.length === 0) {
      debugError('[BundleCheckout] No items to checkout');
      return;
    }

    // Show loading state on button
    if (this.checkoutBtn) {
      this.checkoutBtn.disabled = true;
      this.checkoutBtn.classList.add('is-loading');
      // Store original text for later
      this.checkoutBtn.dataset.originalText = this.checkoutBtn.textContent;
    }

    try {
      // Remove any existing bundle from the cart before adding the new one
      await this.removeExistingBundleItems();

      if (this.selectedCheckoutType === 'subscribe') {
        await this.handleSubscribeCheckout();
      } else {
        await this.handleFullPriceCheckout();
      }

      debugLog('[BundleCheckout] ✅ Cart add successful!');
      
      if (this.settings.afterAddBehavior === 'open_cart') {
        await this.refreshAndOpenCartDrawer();

        if (this.checkoutBtn) {
          this.checkoutBtn.disabled = false;
          this.checkoutBtn.classList.remove('is-loading');
        }
      } else {
        window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
      }

    } catch (error) {
      debugError('[BundleCheckout] Checkout error:', error);
      debugError('[BundleCheckout] Error message:', error?.message);
      debugError('[BundleCheckout] Error stack:', error?.stack);
      
      // Reset button state
      if (this.checkoutBtn) {
        this.checkoutBtn.disabled = false;
        this.checkoutBtn.classList.remove('is-loading');
      }

      // Show error to user
      alert(this.settings.errorMessage);
    }
  }

  /**
   * Refresh cart drawer content and open it
   * Uses the response from cart add which includes sections
   */
  async refreshAndOpenCartDrawer() {
    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const response = this.lastCartResponse;
      
      if (!response) {
        debugWarn('[BundleCheckout] No cart response available');
        return;
      }
      
      debugLog('[BundleCheckout] Updating cart drawer with response:', response);

      // Remove is-empty class if present (same as product-form.js)
      if (cartDrawer && cartDrawer.classList.contains('is-empty')) {
        cartDrawer.classList.remove('is-empty');
      }
      
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        // Use the cart drawer's built-in method to render and open
        // This is exactly what product-form.js does
        cartDrawer.renderContents(response);
        debugLog('[BundleCheckout] Cart drawer updated and opened via renderContents');
      } else {
        debugWarn('[BundleCheckout] Cart drawer not found or renderContents not available');
      }

      // Publish cart update event for any other listeners (same as product-form.js)
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'bundle-checkout',
          productVariantId: this.bundleData?.bundleVariantId,
          cartData: response
        });
      }

    } catch (error) {
      debugError('[BundleCheckout] Error refreshing cart drawer:', error);
    }
  }

  /**
   * Handle Subscribe & Save checkout
   * Adds the bundle product with its own Recharge selling plan
   * Cart properties include Regal tier info for post-purchase processing
   */
  async handleSubscribeCheckout() {
    // Get bundle data from sessionStorage
    const bundleVariantId = this.bundleData?.bundleVariantId;
    const bundleSellingPlanId = this.bundleData?.bundleSellingPlanId; // Bundle's own Recharge plan
    const plan = this.bundleData?.plan; // Regal tier info for cart properties
    
    if (!bundleVariantId) {
      throw new Error('No bundle variant ID found in bundle data');
    }
    
    if (!bundleSellingPlanId) {
      debugWarn('[BundleCheckout] No bundle selling plan ID found, adding without subscription');
    }

    // Build bundle items object for properties (backend processing)
    const bundleItemsObj = {};
    this.bundleData.items.forEach(item => {
      bundleItemsObj[item.productHandle] = item.variantId;
    });

    // Build bundle items display string for cart drawer (Liquid-parseable format)
    // Format: "productTitle|variantTitle|image||..." (|| separates items, | separates fields)
    const bundleItemsDisplay = this.bundleData.items
      .map(item => {
        // Clean product title (remove vendor if present, and escape pipes)
        const cleanTitle = removeVendorFromTitle(item.productTitle || '', item.productVendor || '').replace(/\|/g, '-');
        const variantTitle = (item.variantTitle || '').replace(/\|/g, '-');
        const image = item.featuredImage || '';
        return `${cleanTitle}|${variantTitle}|${image}`;
      })
      .join('||');

    // Calculate total items price (sum of individual item prices) for cart drawer strikethrough
    // This matches getTotalItemsPrice() used for the "full price" display
    const bundleItemsTotal = this.getTotalItemsPrice();

    // Use tier name from plan data (Regal tier for post-purchase processing)
    const tierName = plan?.tierName || 'Standard';

    // Bundle product title for cart drawer display (overrides Shopify's default product title)
    const bundleTitle = this.bundleData?.bundleProductTitle || '';

    // Build the cart item - bundle product with its own Recharge selling plan
    const cartItem = {
      id: bundleVariantId,
      quantity: 1,
      properties: {
        '_bundle_plan': tierName,           // Regal tier name for post-purchase conversion
        '_bundle_title': bundleTitle,       // Bundle look name for cart drawer display
        '_bundle_items': JSON.stringify(bundleItemsObj),
        '_bundle_items_display': bundleItemsDisplay, // For cart drawer display (Liquid-parseable format)
        '_bundle_items_total': bundleItemsTotal,     // Sum of individual item prices (in cents) for strikethrough
        '_order_tag': 'SYS'                 // Tag for backend processing
      }
    };

    // Add bundle's Recharge selling plan (NOT the Regal plan's selling plan)
    if (bundleSellingPlanId) {
      cartItem.selling_plan = bundleSellingPlanId;
    }

    debugLog('[BundleCheckout] ========== ADDING SUBSCRIPTION ==========');
    debugLog('[BundleCheckout] Bundle variant ID:', bundleVariantId);
    debugLog('[BundleCheckout] Bundle selling plan ID:', bundleSellingPlanId);
    debugLog('[BundleCheckout] Regal tier name (for properties):', tierName);
    debugLog('[BundleCheckout] Bundle items:', bundleItemsObj);
    debugLog('[BundleCheckout] Cart item to add:', JSON.stringify(cartItem, null, 2));

    // Get cart drawer for sections to render (same pattern as product-form.js)
    const cartDrawer = document.querySelector('cart-drawer');
    
    // Build form data (same pattern as product-form.js uses)
    const formData = new FormData();
    formData.append('id', bundleVariantId);
    formData.append('quantity', '1');
    
    // Add selling plan if present
    if (bundleSellingPlanId) {
      formData.append('selling_plan', bundleSellingPlanId);
    }
    
    // Add properties
    formData.append('properties[_bundle_plan]', tierName);
    formData.append('properties[_bundle_title]', bundleTitle);
    formData.append('properties[_bundle_items]', JSON.stringify(bundleItemsObj));
    formData.append('properties[_bundle_items_display]', bundleItemsDisplay);
    formData.append('properties[_bundle_items_total]', bundleItemsTotal);
    formData.append('properties[_order_tag]', 'SYS');
    
    // Request sections to be returned (key to updating cart drawer)
    // Match exactly what product-form.js does
    if (cartDrawer) {
      if (typeof cartDrawer.getSectionsToRender === 'function') {
        const sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        debugLog('[BundleCheckout] Requesting sections:', sections);
        formData.append('sections', sections);
        formData.append('sections_url', window.location.pathname);
      }
      if (typeof cartDrawer.setActiveElement === 'function') {
        cartDrawer.setActiveElement(document.activeElement);
      }
    }

    // Use same fetch config as product-form.js
    debugLog('[BundleCheckout] Sending request to cart/add.js...');
    debugLog('[BundleCheckout] FormData entries:');
    for (const [key, value] of formData.entries()) {
      debugLog(`  ${key}:`, value);
    }
    
    const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/javascript'
      },
      body: formData
    });

    debugLog('[BundleCheckout] Response status:', response.status, response.statusText);
    
    const responseData = await response.json();
    debugLog('[BundleCheckout] Response data:', responseData);
    
    if (responseData.status) {
      // Error response (Shopify returns status field on errors)
      debugError('[BundleCheckout] ❌ Cart add failed:', responseData);
      throw new Error(responseData.description || responseData.message || 'Failed to add to cart');
    }

    debugLog('[BundleCheckout] ✅ Subscription added successfully');
    
    // Store response for cart drawer update
    this.lastCartResponse = responseData;
  }

  /**
   * Handle Pay Full Price checkout
   * Adds each item individually to cart
   */
  async handleFullPriceCheckout() {
    // Build items array for bulk add
    const items = this.bundleData.items.map(item => ({
      id: item.variantId,
      quantity: 1
    }));

    debugLog('[BundleCheckout] ========== ADDING FULL PRICE ITEMS ==========');
    debugLog('[BundleCheckout] Items to add:', JSON.stringify(items, null, 2));

    // Get cart drawer for sections to render (same pattern as subscribe checkout)
    const cartDrawer = document.querySelector('cart-drawer');
    
    // Build request body with sections for cart drawer update
    const requestBody = { items };
    
    // Request sections to be returned (key to updating cart drawer)
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      const sections = cartDrawer.getSectionsToRender().map((section) => section.id);
      debugLog('[BundleCheckout] Requesting sections:', sections);
      requestBody.sections = sections;
      requestBody.sections_url = window.location.pathname;
      
      if (typeof cartDrawer.setActiveElement === 'function') {
        cartDrawer.setActiveElement(document.activeElement);
      }
    }

    const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/javascript'
      },
      body: JSON.stringify(requestBody)
    });

    const responseData = await response.json();
    
    if (!response.ok || responseData.status) {
      debugError('[BundleCheckout] ❌ Cart add failed:', responseData);
      throw new Error(responseData.description || responseData.message || 'Failed to add to cart');
    }

    debugLog('[BundleCheckout] ✅ Items added successfully');
    debugLog('[BundleCheckout] Response:', responseData);
    
    // Store response for cart drawer update (same as subscribe checkout)
    this.lastCartResponse = responseData;
  }

  // ============================================
  // Debugging Methods
  // ============================================

  /**
   * Debug: Log current state
   */
  debugState() {
    debugLog('[BundleCheckout] Debug State:', {
      bundleData: this.bundleData,
      selectedCheckoutType: this.selectedCheckoutType,
      planVariantId: this.dataset.planVariantId,
      planSellingPlanId: this.dataset.planSellingPlanId
    });
  }

  /**
   * Debug: Reload bundle data
   */
  reload() {
    this.loadBundleData();
  }
}

// Register the custom element
if (!customElements.get('bundle-checkout')) {
  customElements.define('bundle-checkout', BundleCheckout);
}

// Expose for debugging
window.BundleCheckout = BundleCheckout;

